import React, { useState } from "react";
import { fmtDate } from "../utils/buyerMapping";
import { getAccountsUser } from "../services/modulesApi"; // 2026-10-07 accounts
import StyleProfitLookup from "./StyleProfitLookup"; // 2026-10-20 style profit lookup panel

// TODO(2026-10-03): owner's standing preference is a bottom nav bar on mobile instead of this sidebar/drawer.
// Suggested as its own scoped task (nav component + collapse logic + every view's layout) — not done in the ops-hub revamp.
// Note: "Returns Scan" moved out of here into the Warehouse Returns Processing module (see App.js / AppLauncher.js).
//
// 2026-10-04 declutter (owner rule "Organise features, don't pile them up"): the ~19 flat items are now ONE data-driven list
// (NAV below) grouped under collapsible headers in workflow order. Visibility rules are unchanged per item, except
// Blacklisted Styles in the collapsed rail now follows the expanded rule (!readOnly) — the two used to disagree.
// To add a feature: put it in the right group here; don't add a new top-level item.
//
// 2026-10-12 flow re-order (FLOW_CONTRACT.md "Navigation"): groups are now the reorder workflow stages, each labelled with
// who acts (Monitor -> Raise -> Buyer decision -> Order to vendors -> Follow-up -> Rules & masters). Every item keeps its
// exact visibility rule; only order, grouping and default-open state changed. "+ New" moved from the header into
// "Raise the reorder" as "+ New reorder", and Upload history became a collapsible block inside that group.
// 2026-10-12 remove-pin: the PIN-era Ecom Team / Shailendra Team pages are gone (people are managed in Settings > Users & Access).
// TODO(2026-10-12): who acts for "Order to vendors" / "Follow-up" was not specified by the owner; "Reorder team" is a guess.
// Open/closed groups are remembered per signed-in person (key below + user); the old shared "reorder_nav_groups" key is unused.
const GROUP_KEY = "reorder_nav_groups_v2";

// Per-person storage key: the signed-in email (or name); falls back to login + display name if the user is not loaded yet.
function groupStoreKey() {
  try {
    const u = getAccountsUser();
    const who = u ? (u.email || u.name || "") : `${localStorage.getItem("reorder_buyer") || ""}|${localStorage.getItem("reorder_display_name") || ""}`;
    return `${GROUP_KEY}:${who}`;
  } catch (_) { return GROUP_KEY; }
}
function readGroups(key) {
  try { return JSON.parse(localStorage.getItem(key) || "{}") || {}; } catch (_) { return {}; }
}
// Which stages start open for this login (until the person toggles them). Buyers: their decision stage; Ecom: monitor +
// raise; admin-level logins (incl. sees-all accounts): everything except Rules & masters.
function defaultOpen(role, key) {
  if (role === "ecom") return key === "monitor" || key === "raise" || key === "history";
  if (role === "buyer") return key === "buyer";
  return key !== "rules";
}

const countBadge = (n, small) => n > 0 && (
  <span style={small
    ? { position: "absolute", top: 2, right: 2, background: "var(--danger)", color: "#fff", borderRadius: "50%", fontSize: 9, fontWeight: 800, width: 14, height: 14, display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }
    : { fontSize: 10, fontWeight: 800, background: "var(--danger)", color: "#fff", borderRadius: 10, padding: "1px 6px", lineHeight: 1.4 }}>{n}</span>
);

// 2026-10-08 UI pass: line icons instead of emoji (one stroke style, inherits currentColor).
const IC = {
  overall: <><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></>,
  orders: <><path d="m12 3 9 5-9 5-9-5z" /><path d="m3 13 9 5 9-5" /></>,
  responses: <><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.5 5h13L22 12v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-6z" /></>,
  expired: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  score: <><path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2L12 17.3 6.4 20.2l1.1-6.2L3 9.6l6.2-.9z" /></>,
  pdfs: <><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6M8 13h8M8 17h5" /></>,
  approvals: <><circle cx="12" cy="12" r="9" /><path d="m8 12 3 3 5-6" /></>,
  cp: <><path d="M7 4h10M7 9h10M14 20 7 13h2.5a4.5 4.5 0 0 0 0-9" /></>,
  blacklist: <><circle cx="12" cy="12" r="9" /><path d="m5.6 5.6 12.8 12.8" /></>,
  colours: <><circle cx="12" cy="12" r="9" /><circle cx="8" cy="10" r="1.2" /><circle cx="12" cy="7.5" r="1.2" /><circle cx="16" cy="10" r="1.2" /><path d="M12 21a3 3 0 0 1 0-6h1.5" /></>,
  nobarcode: <><path d="M4 6v12M8 6v12M12 6v5M16 6v12M20 6v12M3 3l18 18" /></>,
  vendors: <><path d="M3 21V9l6-4v4l6-4v4l6-4v16z" /><path d="M7 17h2M11 17h2M15 17h2" /></>,
  barcodes: <><path d="M4 6v12M7 6v12M11 6v12M14 6v12M18 6v12M20 6v12" /></>,
  admin: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></>,
  calendar: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 10h18" /></>,
  image: <><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="2" /><path d="m21 16-5-5-9 9" /></>,
  imageOff: <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 3l18 18" /></>,
  logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5M21 12H9" /></>,
  eye: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></>,
  eyeOff: <><path d="M2 12s3.5-7 10-7c2 0 3.7.7 5.1 1.6M22 12s-3.5 7-10 7c-2 0-3.7-.7-5.1-1.6" /><path d="M3 3l18 18" /></>,
  close: <><path d="M6 6l12 12M18 6 6 18" /></>,
  plus: <><path d="M12 5v14M5 12h14" /></>,
  search: <><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></>,
};
const Ico = ({ n, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0, display: "block" }}>{IC[n]}</svg>
);

const NAV_CSS = `
.nav-group-h { display:flex; align-items:center; gap:6px; width:100%; background:none; border:none; cursor:pointer; padding:12px 14px 4px; font-size:11px; font-weight:650; letter-spacing:.06em; text-transform:uppercase; color:var(--text-muted); text-align:left; transition: color var(--t-fast); }
.nav-group-h:hover { color: var(--text); }
.nav-group-h:focus-visible, .nav-item:focus-visible { outline:none; box-shadow: var(--focus-ring); border-radius:6px; }
.nav-chev { margin-left:auto; font-size:8px; transition: transform .15s; opacity:.7; }
.nav-item { display:flex; align-items:center; gap:10px; width:calc(100% - 12px); margin:1px 6px; background:none; border:none; cursor:pointer; text-align:left; padding:7px 8px; border-radius:6px; font-size:13px; color:var(--text); transition: background var(--t-fast), color var(--t-fast); position:relative; }
.nav-item:hover { background: var(--bg-hover); }
.nav-item.active { background: var(--accent-soft); color: var(--accent); font-weight:600; }
.nav-group-h .nav-who { font-weight:500; letter-spacing:.02em; text-transform:none; font-size:11px; opacity:.8; }
.nav-group-h .nav-glabel { min-width:0; overflow-wrap:anywhere; }
.nav-sub-h { display:flex; align-items:center; gap:10px; width:calc(100% - 12px); margin:1px 6px; background:none; border:none; cursor:pointer; text-align:left; padding:7px 8px; border-radius:6px; font-size:13px; color:var(--text); transition: background var(--t-fast); }
.nav-sub-h:hover { background: var(--bg-hover); }
.nav-sub-h:focus-visible { outline:none; box-shadow: var(--focus-ring); }
.nav-sub-h .nav-ic { width:18px; display:inline-flex; justify-content:center; flex-shrink:0; color: var(--text-muted); }
.nav-new .nav-ic { color: var(--accent); }
@media (max-width: 640px) { .sidebar:not(.sidebar-collapsed) { width: min(var(--sidebar-w), 86vw); } .nav-item, .nav-sub-h { font-size:12.5px; padding:6px 8px; } }
.nav-item .nav-ic { width:18px; display:inline-flex; justify-content:center; flex-shrink:0; color: var(--text-muted); }
.nav-item.active .nav-ic { color: var(--accent); }
`;

export default function Sidebar({ sessions, activeId, onSelect, onNew, canUpload, onDelete, onToggleConfirmation, onOverall, overallActive, onConsolidated, consolidatedActive, expiredActive, onExpired, scoreActive, onScore, vendorMasterActive, onVendorMaster, vendorResponsesActive, onVendorResponses, loadImages, onToggleImages, isAdmin, isEcom, onSettings, settingsActive, onBlacklist, blacklistActive, onNewColours, newColoursActive, onPendingApprovals, pendingApprovalsActive, pendingCount, onCpApprovals, cpApprovalsActive, cpApprovalsCount = 0, onApiExportLog, apiExportLogActive,
  onBarcodes, barcodesActive,
  onNoBarcodeQueue, noBarcodeQueueActive, noBarcodeCount = 0,
  onVendorPdfs, vendorPdfsActive,
  onWaReports, waReportsActive,
  onLogout, readOnly, newActive = false, showStyleProfit = false }) {
  const [collapsed, setCollapsed] = useState(true);
  const [profitOpen, setProfitOpen] = useState(() => { try { return localStorage.getItem("reorder_nav_style_profit") === "1"; } catch (_) { return false; } });
  const toggleProfit = (v) => { setProfitOpen(v); try { localStorage.setItem("reorder_nav_style_profit", v ? "1" : "0"); } catch (_) { /* preference only */ } };
  const storeKey = groupStoreKey();
  const [groupState, setGroupState] = useState(() => readGroups(storeKey));
  const role = isEcom ? "ecom" : isAdmin ? "admin" : "buyer";
  // Admin-only nav items (Settings, Vendor Master, Barcodes, etc.). 2026-10-12: the PIN-era Approver login (sees all, no
  // management pages) is gone, so this is simply isAdmin ("sees everything": admin / ecom / all-departments buyer).
  const isAdminOnly = isAdmin;

  // Workflow order (2026-10-12): monitor -> raise -> buyer decision -> order to vendors -> follow-up -> rules & masters.
  // `show` rules are copied unchanged from the previous grouping.
  const NAV = [
    { key: "monitor", label: "Monitor", who: "Ecom", items: [
      { icon: "overall", label: "Overall Summary", sub: "All sessions · aggregated", on: onOverall, active: overallActive, show: true },
    ] },
    { key: "raise", label: "Raise the reorder", who: "Reorder team", history: true, items: [
      { icon: "plus", label: "New reorder", sub: "Upload a new reorder file", on: onNew, active: newActive, show: !!canUpload, cls: "nav-new" },
      { icon: "approvals", label: "Pending Approvals", sub: "Review & approve reorder runs", on: onPendingApprovals, active: pendingApprovalsActive, show: isAdmin, count: pendingCount || 0 },
    ] },
    { key: "buyer", label: "Buyer decision", who: "Buyers", items: [
      { icon: "orders", label: "All Orders", sub: "All styles · merged", on: onConsolidated, active: consolidatedActive, show: true },
      { icon: "cp", label: "CP Value Approvals", sub: "Line items over ₹1,00,000 CP value", on: onCpApprovals, active: cpApprovalsActive, show: isAdmin, count: cpApprovalsCount || 0 },
      { icon: "colours", label: "New Colours", sub: "Blockbuster — mark ordered colours", on: onNewColours, active: newColoursActive, show: !readOnly },
    ] },
    { key: "vendors", label: "Order to vendors", who: "Reorder team", items: [
      { icon: "pdfs", label: "Vendor PDFs", sub: "Buyer-wise reorder PDFs & drafts", on: onVendorPdfs, active: vendorPdfsActive, show: isAdminOnly },
      { icon: "responses", label: "Vendor Responses", sub: "Accepted / Rejected", on: onVendorResponses, active: vendorResponsesActive, show: true },
      { icon: "nobarcode", label: "No Barcode Queue", sub: "Styles blocked — set barcodes to release", on: onNoBarcodeQueue, active: noBarcodeQueueActive, show: isAdminOnly, count: noBarcodeCount || 0 },
    ] },
    { key: "followup", label: "Follow-up", who: "Reorder team", items: [
      { icon: "expired", label: "Expired POs", sub: "Past validity date", on: onExpired, active: expiredActive, show: true },
      { icon: "score", label: "Vendor Score", sub: "Fulfillment rating", on: onScore, active: scoreActive, show: true },
    ] },
    { key: "rules", label: "Rules & masters", who: "Admin", items: [
      { icon: "blacklist", label: "Blacklisted Styles", sub: "Discontinued / unavailable", on: onBlacklist, active: blacklistActive, show: !readOnly },
      { icon: "vendors", label: "Vendor Master", sub: "Manage & send links", on: onVendorMaster, active: vendorMasterActive, show: !isEcom },
      { icon: "barcodes", label: "Barcodes", sub: "Style+size barcode cache", on: onBarcodes, active: barcodesActive, show: isAdminOnly },
      // 2026-10-09: "Admin" became "Reorder settings" (Reorder rules only). Users, notifications, integrations, PINs and
      // PIN teams moved to the app-level Settings view in the header user menu (AdminPortal scope="app").
      { icon: "admin", label: "Reorder settings", sub: "Excess threshold · IT auto-forward · CP profitability", on: onSettings, active: settingsActive, show: isAdminOnly && !isEcom },
    ] },
  ].map((g) => ({ ...g, items: g.items.filter((i) => i.show && i.on) })).filter((g) => g.items.length || g.history);

  // A group holding the current page (or, for Raise, the open upload session) is always shown open.
  const historyActive = !!activeId && sessions.some((x) => x._id === activeId);
  const openIn = (gs, g) => g.items.some((i) => i.active) || (g.history && historyActive) || (gs[g.key] !== undefined ? gs[g.key] : defaultOpen(role, g.key));
  const historyOpenIn = (gs) => historyActive || (gs.history !== undefined ? gs.history : defaultOpen(role, "history"));
  const isOpen = (g) => openIn(groupState, g);
  const historyOpen = historyOpenIn(groupState);
  // Functional update: two quick toggles in one tick must not overwrite each other.
  const saveGroups = (change) => setGroupState((gs) => {
    const next = { ...gs, ...change(gs) };
    try { localStorage.setItem(storeKey, JSON.stringify(next)); } catch (_) { /* preference only: storage blocked -> still works for this visit */ }
    return next;
  });
  const toggleGroup = (g) => saveGroups((gs) => ({ [g.key]: !openIn(gs, g) }));
  const toggleHistory = () => saveGroups((gs) => ({ history: !historyOpenIn(gs) }));

  if (collapsed) {
    return (
      <div className="sidebar sidebar-collapsed">
        <style>{NAV_CSS}</style>
        <button className="collapse-btn" title="Expand sidebar" aria-label="Expand sidebar" onClick={() => setCollapsed(false)}>›</button>
        {showStyleProfit && (
          <button className="session-dot" title="Style profit lookup" aria-label="Style profit lookup" onClick={() => { toggleProfit(true); setCollapsed(false); }}><Ico n="search" size={17} /></button>
        )}
        {NAV.map((g, gi) => (
          <React.Fragment key={g.key}>
            {gi > 0 && <div title={g.label} style={{ width: 24, height: 1, background: "var(--border)", margin: "3px 0" }} />}
            {g.items.filter((i) => i.icon !== "plus").map((i) => (
              <button key={i.label} className={`session-dot ${i.active ? "active" : ""}`} title={`${g.label} › ${i.label}${i.count ? ` (${i.count})` : ""}`}
                aria-label={i.label} onClick={i.on} style={{ position: "relative" }}>
                <Ico n={i.icon} size={17} />{countBadge(i.count, true)}
              </button>
            ))}
          </React.Fragment>
        ))}
        <div style={{ width: 24, height: 1, background: "var(--border)", margin: "3px 0" }} />
        <button className="btn-new-icon" title="New upload" onClick={onNew}>+</button>
        {sessions.map((s) => (
          <div key={s._id} className={`session-dot ${s._id === activeId ? "active" : ""}`} title={`${fmtDate(s.request_date)} — ${s.filename}`} onClick={() => onSelect(s._id)}><Ico n="calendar" /></div>
        ))}
        <div style={{ flex: 1 }} />
        <button className="session-dot" title={`Images: ${loadImages ? "On" : "Off"}`} aria-label={`Images: ${loadImages ? "On" : "Off"}`} onClick={onToggleImages}><Ico n={loadImages ? "image" : "imageOff"} /></button>
        <button className="session-dot" title="Log out" aria-label="Log out" onClick={onLogout} style={{ color: "var(--danger)" }}><Ico n="logout" /></button>
      </div>
    );
  }

  return (
    <div className="sidebar">
      <style>{NAV_CSS}</style>
      <div className="sidebar-header">
        <h3>Navigation</h3>
        <div style={{ display: "flex", gap: 6 }}>
          <button className="collapse-btn-inline" title="Collapse sidebar" aria-label="Collapse sidebar" onClick={() => setCollapsed(true)}>‹</button>
        </div>
      </div>

      <div className="sidebar-scroll">
        {showStyleProfit && (
          <div style={{ marginBottom: 2 }}>
            <button className="nav-group-h" onClick={() => toggleProfit(!profitOpen)} aria-expanded={profitOpen} title="Profit per piece for any style, by channel">
              <span className="nav-glabel" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Ico n="search" size={12} />Style profit</span>
              <span className="nav-chev" style={{ transform: profitOpen ? "rotate(0deg)" : "rotate(-90deg)" }}>▼</span>
            </button>
            {profitOpen && <div style={{ padding: "4px 12px 8px" }}><StyleProfitLookup compact autoFocus /></div>}
          </div>
        )}
        {NAV.map((g) => {
          const open = isOpen(g);
          const groupCount = g.items.reduce((a, i) => a + (i.count || 0), 0);
          return (
            <div key={g.key} style={{ marginBottom: 2 }}>
              <button className="nav-group-h" onClick={() => toggleGroup(g)} aria-expanded={open} title={`${g.label} · ${g.who}`}>
                <span className="nav-glabel">{g.label} <span className="nav-who">· {g.who}</span></span>
                {!open && countBadge(groupCount)}
                <span className="nav-chev" style={{ transform: open ? "rotate(0deg)" : "rotate(-90deg)" }}>▼</span>
              </button>
              {open && g.items.map((i) => (
                <button key={i.label} className={`nav-item ${i.cls || ""} ${i.active ? "active" : ""}`} onClick={i.on} title={i.sub}>
                  <span className="nav-ic"><Ico n={i.icon} /></span>
                  <span style={{ flex: 1, minWidth: 0 }}>{i.label}</span>
                  {countBadge(i.count)}
                </button>
              ))}
              {open && g.history && (
                <>
                  <button className="nav-sub-h" onClick={toggleHistory} aria-expanded={historyOpen} title="Every uploaded reorder file · click one to open it">
                    <span className="nav-ic"><Ico n="calendar" /></span>
                    <span style={{ flex: 1, minWidth: 0 }}>Upload history</span>
                    <span className="session-badge">{sessions.length}</span>
                    <span className="nav-chev" style={{ marginLeft: 0, transform: historyOpen ? "rotate(0deg)" : "rotate(-90deg)" }}>▼</span>
                  </button>
                  {historyOpen && (
                    <div className="sidebar-list">
                      {sessions.length === 0 && (
                        <div className="sidebar-empty">No uploads yet.<br />Click "+ New reorder" to start.</div>
                      )}
                      {sessions.map((s) => (
                        <div key={s._id} className={`session-item ${s._id === activeId ? "active" : ""}`} onClick={() => onSelect(s._id)}>
                          <div className="session-item-top">
                            <span className="session-date">{fmtDate(s.request_date)}</span>
                            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                              <span className="session-badge">{s.total_styles} styles</span>
                              {onToggleConfirmation && (
                                <button
                                  className="delete-btn"
                                  title={s.confirmation_enabled !== false ? "Hide from vendor confirmation" : "Show to vendor confirmation"}
                                  onClick={(e) => { e.stopPropagation(); onToggleConfirmation(s._id); }}
                                  aria-label={s.confirmation_enabled !== false ? "Hide from vendor confirmation" : "Show to vendor confirmation"}
                                  style={{ opacity: s.confirmation_enabled !== false ? 1 : 0.6, color: s.confirmation_enabled !== false ? undefined : "var(--danger)" }}
                                >
                                  <Ico n={s.confirmation_enabled !== false ? "eye" : "eyeOff"} size={14} />
                                </button>
                              )}
                              {onDelete && (
                                <button className="delete-btn" title="Delete" aria-label="Delete session" onClick={(e) => { e.stopPropagation(); onDelete(s._id); }}><Ico n="close" size={14} /></button>
                              )}
                            </div>
                          </div>
                          <div className="session-meta" title={s.filename}>{s.filename}</div>
                          <div className="session-meta" style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>{fmtDate(s.upload_date)}</span>
                            {s.confirmation_enabled === false && <span style={{ fontSize: 10.5, color: "var(--danger)", fontWeight: 600 }}>Hidden from vendors</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>{/* end sidebar-scroll */}

      <div className="sidebar-settings">
        <div className="settings-row">
          <span className="settings-label" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Ico n="image" size={15} />Load images</span>
          <button className={`toggle-btn ${loadImages ? "on" : ""}`} role="switch" aria-checked={!!loadImages} aria-label="Load images" onClick={onToggleImages} title={loadImages ? "Click to disable image loading" : "Click to enable image loading"}>
            <span className="toggle-knob" />
          </button>
        </div>
        <button className="sidebar-logout" onClick={onLogout}>
          <Ico n="logout" size={15} />Log out
        </button>
      </div>
    </div>
  );
}
